import React, { useEffect, useRef, useState } from 'react'
import ICTSelectorOMS from './ICT/ICTSelectorOMS';
import { ButtonDelete, ButtonPlus } from '../mini_components';
import { Card, CardContent, Grid, PropTypes, Typography } from '@mui/material';
import { useSelectSmartField, useUpdateEffect } from '../../../hooks';
import { LocalizeContextProps, Translate, withLocalize } from 'react-localize-redux';
import { EnhancedTable } from '../EnhancedTable';
import ICTSelectorGeneral from './ICT';
import Background from './Background';
import Allergy from './Allergy';
import FamilyBackground from './FamilyBackground';
import SingleTreatmentSelector from './SingleTreatmentSelector';
import BMIField from './BMIField';
import EDDField from './EDDField';
import { isInteger } from 'lodash';
import RequestField from './RequestField';

import { MEDICAL_HISTORY_FIELDS } from '../../../constants';
import { getDateFromStringOrDate } from '../../../utils/bill';
import { differenceDatesToString, stringDatePostgresToDate, canEditSubmission } from '../../../utils';
import { parseUTCString } from '../../../utils/dateUtils';
import MedicalHistoryAI, { TemplatedMedicalHistoryEditor } from './Templated';
import { TemplatedMedicalHistoryEditorType } from './Templated/TemplatedMedicalHistoryEditor';
import { fetchTemplate, fetchTemplates } from '../../../services/surveys';

import Modal from '../modal';
import { TextField, Box, Snackbar, Alert, Tooltip, Checkbox, FormControlLabel } from '@mui/material';
import { deleteSubmissionPatientAction } from '../../../redux/actions/submissionsPatientActions';
import { useDispatch, useSelector } from 'react-redux';
import Loader from '../../Loader';
import { usePrevious, useResearcher, useDepartments } from '../../../hooks';
import { dateToFullDateString, dateTimeFromDate, researcherFullName, dateAndTimeFromPostgresString } from '../../../utils/index.jsx';
import { useSnackBarState } from "../../../hooks";
import { PERMISSION } from '../../../components/investigation/share/user_roles';

export interface PropsSmartField {
    variant:"standard" | "filled" | "outlined" | undefined,
    size?:string,
    language:string,
    typeMargin:PropTypes.Margin | undefined,
    type:string,
    slaves?:object[],
    country?:string,
    template? : string,
    cancel?: (() => void),
    updateElement?: (index:number, element:SmartFieldType) => void,
    elementSelected: (element:SmartFieldType) => void,
    error:boolean,
    onSaveToDatabase?: (treatmentData: any) => Promise<void>,
    onUpdateDatabase?: (treatmentData: any) => Promise<void>
    onDeleteDatabase?: (treatmentData: any) => Promise<void>,
    editingElement?: SmartFieldType,
    editingIndex?: number | null
}
export interface PropsSmartFieldLocalized extends PropsSmartField, LocalizeContextProps{

}
export interface PropsIct extends PropsSmartField{
    
    diagnose:SmartFieldType | null,
    setError:(error:boolean) => void,
}

export interface DrugType{
    name:string,
    id:string
}
export interface AllergyType{
    allergy : string,
    "compo-code" : string
}


export interface BackgroundType{
    background : string,
    "background-code" : string,
    "background-date" : string
}

export interface FamilyBackgroundType{
    "family-background" : string,
    "family-background-code" : string,
    "family-background-relation" ?: string
}

export interface TreatmentType{
    "treatment" : string,
    "drug-code" : string,
    "treatment-posology": string, 
    "treatment-dose": string, 
    "treatment-start" : string, 
    "treatment-finish" : string 
}

export interface TreatmentInpatientSelectionType{
    "treatment_inpatient" : string,
    "drug-code" : string,
    "treatment-frecuency": "",
    "treatment-dose": "",
    "treatment-unit": "",
    "treatment-start":"",
    "treatment-finish":"",
    "uuidResearcher"?: string,
    "createdAt"?: string
}

export interface TreatmentRegularType{
    "treatment_regular" : string,
    "drug-code" : string,
    "treatment_regular-posology": string
}


export interface BMIType{
    "bmi" : number,
    "bmi_height" : number,
    "bmi_weight": number
}

export interface EDDType{
    "edd" : Date,
    "edd_last_period" : Date
}

export interface RequestLabType{
    "request_lab" : string,
    "request_id" : number
}

export interface RequestImageType{
    "request_img" : string,
    request_id: number
}

export interface MedicalHistoryType{
    medical_history_ai : string,
    "medical_history_ai-send_email"?: boolean
}

export interface TemplatedMedicalHistoryType{
    "template_medical_history-template_id" : string,
    "template_medical_history-notes" : string
}

export type SmartFieldType =  TemplatedMedicalHistoryType | Diagnosis | BackgroundType | FamilyBackgroundType | AllergyType | TreatmentType | TreatmentRegularType | BMIType | EDDType | RequestLabType | RequestImageType | MedicalHistoryType;

export interface Diagnosis{
    icd : string,
    "icd-code": string
}

const DATE_FIELDS_FORMAT:{[key: string]: any} = {"background-date" : "YYYY", "treatment-start" : "regular", "treatment-finish" : "regular", edd : "regular", edd_last_period:"regular"};

const NO_TABLE_FIELDS = MEDICAL_HISTORY_FIELDS;
const SINGLE_SMARTFIELDS = ["bmi", "edd", ...MEDICAL_HISTORY_FIELDS];
export const INITIAL_SELECT = ["icd11", "background", "treatment", "treatment_regular", "family-background", "allergy"];

interface Props extends LocalizeContextProps {
    name : string,
    label : string,
    typeMargin : PropTypes.Margin | undefined,
    printMode?:boolean,
    slaves : object[],
    type:string,
    error: boolean,
    mode : string,
    country:string,
    language?:string,
    uuidPatient?:string,
    forceDisplayElements?:boolean,
    template? : string,
    formValues?:any,
    uuidSurvey?:string,
    uuidInvestigation?:string,
    initialState:{
        addingElements:boolean,
        listElements:Diagnosis[]
    },
    fieldInfo?: any,
    idSubmission?: number,
    onSaveToDatabase?: (treatmentData: any) => Promise<void>
    onUpdateDatabase?: (treatmentData: any) => Promise<void>
    onDeleteDatabase?: (treatmentData: any) => Promise<void>
    updateElement?: (index:number, element:SmartFieldType) => void,
    elementSelected: (treatments:SmartFieldType[] | boolean) => void,
}

const TRANSLATED_COLUMNS = ["treatment-posology", "treatment-frecuency"]

// Helper function to get prescriber tooltip content
const getPrescribedByTooltip = (uuidResearcher: string | undefined, createdAt: string | undefined, language: string, researchers: any[]) => {
    if (!uuidResearcher) {
        return '';
    }
    
    const researcher = researchers.find((r: any) => r.uuid === uuidResearcher);
    
    if (!researcher) {
        return 'Unknown prescriber';
    }
    
    const researcherName = researcherFullName(researcher);
    const dateStr = createdAt ? dateAndTimeFromPostgresString(language, createdAt) : '';
    
    if (dateStr) {
        return (
            <div style={{ textAlign: 'left' }}>
                <div>Prescribed by: {researcherName}</div>
                <div style={{ fontSize: '0.9em', marginTop: '4px' }}>{dateStr}</div>
            </div>
        );
    }
    
    return `Prescribed by: ${researcherName}`;
};

const SmartField:React.FC<Props> = (props) => {
    const dispatch = useDispatch();
    const isMountedRef = useRef(true);

    const [loading, setLoading] = useState(false);
    const [operationTimestamp, setOperationTimestamp] = useState<number | null>(null);          //To track async operations

    // Watch submissions from Redux to detect changes
    const emptyArray: any[] = [];
    const submissionsSurvey = useSelector((state: any) => {         //useSelector extracts from the Redux store
        if (props.uuidPatient && props.uuidSurvey && state.patientsSubmissions.data && 
            state.patientsSubmissions.data[props.uuidPatient] && 
            state.patientsSubmissions.data[props.uuidPatient][props.uuidSurvey]) {              //This if statement is to safely access the submissions
            return [...state.patientsSubmissions.data[props.uuidPatient][props.uuidSurvey].submissions];
        }
        return emptyArray;
    });
    const prevSubmissionsSurvey = usePrevious(submissionsSurvey, false);                //This stores the previous value of submissionsSurvey from the last render. False makes possible that its value is undefined the first render

    const [listElements, setListElements] = useState<SmartFieldType[]>(props.initialState ? props.initialState.listElements : []);
    // const [addingElements, setAddingElements] = useState(
    //     props.initialState?.listElements?.length > 0 ? false : props.mode === "form"
    // );
    const [addingElements, setAddingElements] = useState(
        props.mode === "form" && !props.forceDisplayElements
    );
    
    const [addElement, renderSelect, resetState ] = useSelectSmartField(props.initialState, props.label, props.type, props.error, setAddingElements);

    const [showStopModal, setShowStopModal] = useState(false);
    const [stopMotive, setStopMotive] = useState("");
    const [treatmentToStop, setTreatmentToStop] = useState<number | null>(null);

    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [removeMotive, setRemoveMotive] = useState("");
    const [treatmentToRemove, setTreatmentToRemove] = useState<number | null>(null);

    const [treatmentToEdit, setTreatmentToEdit] = useState<number | null>(null);
    const [editingElement, setEditingElement] = useState<SmartFieldType | null>(null);

    const [showSnackbar, setShowSnackbar, handleCloseSnackbar] = useSnackBarState();

    // Filter state for showing only active treatments
    const [showOnlyActiveTreatments, setShowOnlyActiveTreatments] = useState(false);

    // Get researchers for prescriber info tooltip
    const { researchers } = useDepartments();

    // Get investigation from Redux store to check permissions
    const investigation = useSelector((state: any) => {
        if (props.uuidInvestigation && state.investigations?.data) {
            return state.investigations.data.find((inv: any) => inv.uuid === props.uuidInvestigation);
        }
        return null;
    });

    // Check if user has permission to stop treatments
    const hasStopTreatmentPermission = investigation?.permissions?.includes(PERMISSION.STOP_TREATMENT) ?? true; // Default to true if investigation not found
    
    // Check if user has permission to delete treatments (submissions)
    const hasDeleteTreatmentPermission = investigation?.permissions?.includes(PERMISSION.DELETE_SUBMISSION) ?? true; // Default to true if investigation not found

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // Helper function to check if a treatment is active
    const isTreatmentActive = (element: any): boolean => {
        // Check if treatment is stopped
        const stoppedAt = element["treatment-stoppedAt"];
        if (stoppedAt && stoppedAt !== "NaN" && stoppedAt !== null && stoppedAt !== undefined) {
            return false; // Treatment is stopped
        }
        
        // Check if treatment is finished (end date has passed)
        const endDate = element["treatment-finish"];
        if (endDate && endDate !== "NaN" && endDate !== null && endDate !== undefined) {
            const endDateTime = new Date(endDate).getTime();
            const now = new Date().getTime();
            
            if (endDateTime < now) {
                return false; // Treatment has finished (end date passed)
            }
        }
        
        return true; // Treatment is active
    };

    function cancel(){
        if(listElements.length === 0){
            resetState();
        }
        else{
            setAddingElements(false);
        }

        if (props.forceDisplayElements) {
            setAddingElements(false);
        }

        // Reset editing state when canceling
        if (treatmentToEdit !== null) {
            setTreatmentToEdit(null);
            setEditingElement(null);
        }
    }
    function renderElements(){
        if(listElements.length > 0 && !addingElements && ! NO_TABLE_FIELDS.includes(props.type)){
            let headCells = [];//[{ id: "name", alignment: "left", label: <Translate id={`hospital.${props.type}-plural`} /> }]
            
            const keys = Object.keys(listElements[0]);
            for(let i = 0; i < keys.length;i++){
                //Para no incluir attributos de los smarfield en la tabla
                if(!["code", "id", "postId", "treatmentPostId"].some(word => keys[i].includes(word))){
                    headCells.push({ id: keys[i], alignment: "left", label: <Translate id={`hospital.${keys[i]}-table`} /> }) 
                }
            }
            if(props.type === "treatment_prescription"){
                headCells = headCells.filter((item) => {
                    if((item.id === "treatment-start" || item.id === "treatment-finish")){
                        return false;
                    }
                    return true;
                });
                headCells.push({ id: "duration", alignment: "left", label: <Translate id={`hospital.treatment-duration`} /> });
            }
            if(props.type === "treatment_inpatient"){

                headCells = [
                    { id: "treatment_inpatient", alignment: "left", label: <Translate id="hospital.treatment_inpatient-table" /> }, // Drug
                    { id: "treatment-route", alignment: "left", label: <Translate id="hospital.treatment-route-table" /> },         // Route
                    { id: "treatment-frecuency", alignment: "left", label: <Translate id="hospital.treatment-frecuency-table" /> }, // Frequency
                    { id: "treatment-dose", alignment: "left", label: <Translate id="hospital.treatment-dose-table" /> },
                    { id: "treatment-unit", alignment: "left", label: <Translate id="hospital.treatment-unit-table" /> },
                    { id: "treatment-start", alignment: "left", label: <Translate id="hospital.treatment-start-table" /> },         // Start date
                    { id: "treatment-finish", alignment: "left", label: <Translate id="hospital.treatment-finish-table" /> },       // End date
                ];

            }
            const rows = listElements.map((element, index) => {
                let valueDict:any = {...element};
                
                // Handle backward compatibility: convert old treatment-dose_amount to treatment-dose for display
                if (props.type === "treatment_inpatient" && element["treatment-dose_amount"] && !element["treatment-dose"]) {
                    valueDict["treatment-dose"] = element["treatment-dose_amount"];
                    valueDict["treatment-unit"] = element["treatment-dose_unit"] || "";
                }
                
                for(const [key, val] of Object.entries(element)) {
                    
                    if(val && DATE_FIELDS_FORMAT.hasOwnProperty(key)){
                        if(props.type === "treatment_prescription"){
                            if(element["treatment-finish"] !== null){
                                const startDate = getDateFromStringOrDate(element["treatment-start"]);
                                const finishDate = getDateFromStringOrDate(element["treatment-finish"]);
                                const objectDifference = differenceDatesToString(startDate, finishDate);
                                
                                const timeUnit = Object.keys(objectDifference)[0];
                                valueDict["duration"] = objectDifference[timeUnit] + " "+props.translate(`hospital.time-unit-options.${timeUnit}`);
                            }
                            else{
                                valueDict["duration"] = props.translate(`hospital.chronic`);
                            }
                        }
                        let dateObject = null;
                        const format = DATE_FIELDS_FORMAT[key] as string;

                        if(val && typeof val.getMonth === 'function'){
                            dateObject = val;
                        }   
                        else if(val && Date.parse(val)){
                            dateObject = stringDatePostgresToDate(val);
                        }

                        if ((element as any)["treatment-finish"] === undefined && props.type === "treatment_inpatient") {
                            if ((element as any)["treatment-stoppedAt"] !== undefined) {
                                const stoppedAtDate = new Date((element as any)["treatment-stoppedAt"]);
                                const formattedStoppedAt = dateTimeFromDate(stoppedAtDate, props.activeLanguage?.code);
                                valueDict["treatment-finish"] = props.translate("hospital.treatment-inpatient-stopped-at") + " " + formattedStoppedAt;
                            }
                        }

                        if (key === "treatment-finish" && props.type === "treatment_inpatient") {
                            const formattedDate = format === "regular" ? dateToFullDateString(dateObject, props.activeLanguage?.code) : dateObject.getFullYear();

                            if ((element as any)["treatment-stoppedAt"] && (element as any)["treatment-stoppedAt"] !== "NaN" && 
                                (element as any)["treatment-stoppedAt"] !== null && (element as any)["treatment-stoppedAt"] !== undefined) {

                                const stoppedAtDate = new Date((element as any)["treatment-stoppedAt"]);
                                const formattedStoppedAt = dateTimeFromDate(stoppedAtDate, props.activeLanguage?.code);
                                valueDict[key] = formattedDate + " (" + props.translate("hospital.treatment-inpatient-stopped-at") + " " + formattedStoppedAt + ")";

                            }

                            else {
                                valueDict[key] = formattedDate;
                            }
                        }

                        else {
                            valueDict[key] = format === "regular" ? dateToFullDateString(dateObject, props.activeLanguage?.code) : dateObject.getFullYear();
                        }

                    }
                    else if( val && TRANSLATED_COLUMNS.includes(key)){
                        const translationKey = key === "treatment-frecuency" 
                            ? `hospital.frecuency-options.${val}`
                            : `hospital.${key}-values.${val}`;
                        const translation = props.translate(translationKey).toString();
                        valueDict[key] = translation.includes('Missing translationId:') ? val : translation;
                    } 
                    
                    else{
                        if(isNaN(val) || isInteger(val)){
                            valueDict[key] = val;
                        }
                        else{
                            valueDict[key] = parseFloat(val).toFixed(2);
                        }
                    }
                }
                valueDict.id = index;
                    
                return valueDict
            });

            // Apply active treatment filter if checkbox is checked
            let filteredRows = rows;
            if (props.type === "treatment_inpatient" && showOnlyActiveTreatments) {
                filteredRows = rows.filter((row, index) => isTreatmentActive(listElements[index]));
            }

            if(props.mode === "form"){

                const actions = [];

                if (props.type === "treatment_inpatient") {
                    // Add info action to show who prescribed the treatment
                    actions.push({
                        "type" : "info",
                        "func" : (index:number) => {
                            // This action doesn't need a function, it's just informational
                        },
                        "check": (row:any) => {
                            // Show info icon if uuidResearcher or stoppedBy is available
                            return !!row.uuidResearcher || !!row["treatment-stoppedBy"];
                        },
                        "tooltip": (row:any) => {
                            const prescribedTooltip = getPrescribedByTooltip(
                                row.uuidResearcher,
                                row.createdAt,
                                props.activeLanguage?.code || 'en',
                                researchers
                            );
                            const stoppedBy = row["treatment-stoppedBy"];
                            if (stoppedBy && row["treatment-stoppedAt"]) {
                                const stopper = researchers.find((r: any) => r.uuid === stoppedBy);
                                const stopperName = stopper ? researcherFullName(stopper) : 'Unknown';
                                const stoppedAtDate = new Date(row["treatment-stoppedAt"]);
                                const formattedStoppedAt = dateTimeFromDate(stoppedAtDate, props.activeLanguage?.code);
                                return (
                                    <div style={{ textAlign: 'left' }}>
                                        {prescribedTooltip && <div>{prescribedTooltip}</div>}
                                        <div style={{ marginTop: prescribedTooltip ? '8px' : '0' }}>Stopped by: {stopperName}</div>
                                        <div style={{ fontSize: '0.9em', marginTop: '4px' }}>{formattedStoppedAt}</div>
                                    </div>
                                );
                            }
                            return prescribedTooltip;
                        }
                    });
                    actions.push({
                        "type" : "edit", 
                        "func" : (index:number) => editTreatment(index), 
                        "check": (row:any) => {
                            // Cannot edit stopped treatments
                            if (row["treatment-stoppedAt"] !== "NaN" && row["treatment-stoppedAt"] !== null && row["treatment-stoppedAt"] !== undefined) {
                                return false;
                            }
                            
                            // Check if within edit window (1 year for treatment_inpatient)
                            if (row.createdAt) {
                                return canEditSubmission(row.createdAt, true);
                            }
                            
                            // If no createdAt, allow editing (new treatment not yet saved)
                            return true;
                        }
                    });
                    actions.push({
                        "type" : "stop", 
                        "func" : (index:number) => stopTreatment(index), 
                        "check": (row:any) => {

                            if (row["treatment-stoppedAt"] !== "NaN" && row["treatment-stoppedAt"] !== null && row["treatment-stoppedAt"] !== undefined) {
                                return false;
                            }
            
                            const endDate = row["treatment-finish"];
                            if (!endDate || endDate === "NaN") {
                                return true;
                            }
                            const endDateTime = new Date(endDate).getTime();
                            const now = new Date().getTime();
                            
                            return endDateTime > now;
                        }
                    });
                }

                actions.push({
                    "type" : "delete", 
                    "func" : (index:number) => removeElement(index),
                    "check": (row:any) => {
                        // Only allow delete if created less than 2 hours ago or if it is not saved in the database yet (it doesnt have createdAt attribute)
                        
                        if (!row.createdAt) {
                            return true;
                        }
            
                        // Use parseUTCString to ensure timezone-aware parsing (server timestamps are in UTC)
                        const createdTime = parseUTCString(row.createdAt).getTime();
                        const now = new Date().getTime(); // getTime() always returns UTC milliseconds
                        const twoHoursInMs = 2 * 60 * 60 * 1000; // 2 hours in milliseconds
                        
                        return (now - createdTime) < twoHoursInMs;
                    }
                });

                return (
                    <Grid item xs={12} spacing={3}>
                        <EnhancedTable 
                            noHeader 
                            noSelectable={true} 
                            rows={filteredRows} 
                            headCells={headCells} 
                            actions={actions}
                        />
                    </Grid>
                );
                    
            }
            else{
                return (
                    <Grid item xs={12}>
                        <EnhancedTable noHeader noSelectable={true} rows={rows} 
                            dense={props.printMode} noFooter={props.printMode} headCells={headCells} />
                    </Grid>
                )
            }   
        }
    }

    function removeElement(id:number){
        // Check permissions before allowing delete
        if (!hasDeleteTreatmentPermission) {
            setShowSnackbar({
                show: true,
                message: 'You do not have permission to delete treatments',
                severity: 'error'
            });
            return;
        }
        
        setTreatmentToRemove(id);
        setRemoveMotive("");
        setShowRemoveModal(true);
    }

    function editTreatment(id:number){
        const treatment = listElements[id];
        // Normalize treatment data for editing - convert old format to new format if needed
        const normalizedTreatment = {...treatment};
        if (props.type === "treatment_inpatient" && treatment["treatment-dose_amount"] && !treatment["treatment-dose"]) {
            // Old format: convert treatment-dose_amount to treatment-dose
            normalizedTreatment["treatment-dose"] = treatment["treatment-dose_amount"];
            normalizedTreatment["treatment-unit"] = treatment["treatment-dose_unit"] || "";
        }
        setTreatmentToEdit(id);
        setEditingElement(normalizedTreatment);
        setAddingElements(true);
    }

    function stopTreatment(id:number){
        // Check permissions before allowing stop
        if (!hasStopTreatmentPermission) {
            setShowSnackbar({
                show: true,
                message: 'You do not have permission to stop treatments',
                severity: 'error'
            });
            return;
        }
        
        setTreatmentToStop(id);
        setStopMotive("");
        setShowStopModal(true);
    }


    async function confirmStopTreatment(){

        if (treatmentToStop !== null) {
            setOperationTimestamp(Date.now());
            setLoading(true);

            let updatedTreatment = {
                ...listElements[treatmentToStop],
                "treatment-stop_motive": stopMotive,
                "treatment-stoppedAt": Date.now().toString()
            }

            if (props.onUpdateDatabase) {

                try {
                    await props.onUpdateDatabase(updatedTreatment);
                    setListElements(listElements.map((item, index) => 
                        index === treatmentToStop ? updatedTreatment : item
                    ));
                    setLoading(false);
                    setOperationTimestamp(null);
                } catch (error) {
                    console.error("Error updating treatment:", error);
                    setLoading(false);
                    setOperationTimestamp(null);
                }
            }

            else {
                setLoading(false); 
            }

        }
        
        // Reset modal state
        setShowStopModal(false);
        setStopMotive("");
        setTreatmentToStop(null);
    }


    async function confirmRemoveTreatment(){
        if (treatmentToRemove !== null) {
            setOperationTimestamp(Date.now());
            setLoading(true);

            const treatment = listElements[treatmentToRemove] as any;
            const submissionId = treatment?.submission_id;

            const postObj = { 
                deletionReason: removeMotive,
                submission_id: submissionId 
            };
            
            if (props.onDeleteDatabase) {

                try {

                    await props.onDeleteDatabase(postObj);

                    if (isMountedRef.current) { 
                        setListElements(listElements.filter((item, index) => index !== treatmentToRemove));
                        setLoading(false);
                        setOperationTimestamp(null);
                    }
                } catch (error: any) {

                    if (isMountedRef.current) {
                        setLoading(false);
                        setOperationTimestamp(null);
                    }

                }

            }

            else {
                if (isMountedRef.current) {
                    setLoading(false); 
                }
            }
        }
        
        // Reset modal state
        if (isMountedRef.current) {
            setShowRemoveModal(false);
            setRemoveMotive("");
            setTreatmentToRemove(null);
        }
    }

    function cancelStopTreatment(){
        setShowStopModal(false);
        setStopMotive("");
        setTreatmentToStop(null);
    }

    function cancelRemoveTreatment(){
        setShowRemoveModal(false);
        setRemoveMotive("");
        setTreatmentToRemove(null);
    }

    function updateElement(id:number, element:SmartFieldType){
         if(listElements.length > 0){
            setListElements(listElements.map((item, index) => index === id ? element : item));
        }
        else{
            setListElements([element]);
        }
    }

    function elementSelected(diagnose:SmartFieldType){
        if (treatmentToEdit !== null && editingElement !== null) {
            // Editing existing treatment - update local state
            if (props.updateElement) {
                props.updateElement(treatmentToEdit, diagnose);
            } else {
                updateElement(treatmentToEdit, diagnose);
            }
            
            // Note: onUpdateDatabase is already called from SingleTreatmentSelector when editing
            // so we don't need to call it here to avoid double calls
            
            setTreatmentToEdit(null);
            setEditingElement(null);
        } else {
            // Adding new treatment
            setListElements(oldArray => [...oldArray, diagnose]);
        }
        setAddingElements(false);
    }
   
    function renderSelector(){
        if((addingElements && props.mode === "form") || NO_TABLE_FIELDS.includes(props.type)){
            const propsSmartField:PropsSmartField = {
                type:props.type, 
                variant:"outlined", 
                typeMargin:props.typeMargin, 
                template : props.template,
                cancel, 
                language:props.language ? props.language : props.activeLanguage.code, 
                country:props.country, 
                error:props.error, 
                slaves:props.slaves,
                size:"small", 
                updateElement:(index:number, smartF:SmartFieldType) => updateElement(index, smartF),
                elementSelected:(smartF:SmartFieldType) => elementSelected(smartF),
                onSaveToDatabase: props.onSaveToDatabase,
                onUpdateDatabase: props.onUpdateDatabase,
                onDeleteDatabase: props.onDeleteDatabase,
                editingElement: treatmentToEdit !== null && editingElement !== null ? editingElement : undefined,
                editingIndex: treatmentToEdit
            }

            let smartField = null;
            switch(props.type){
                case "background":
                    smartField = <Background {...propsSmartField} />
                    break;
                case "family-background":
                    smartField = <FamilyBackground {...propsSmartField} />        
                    break;
                case "allergy":
                    smartField = <Allergy {...propsSmartField}/>
                    break;
                case "icd11":
                    smartField = <ICTSelectorGeneral {...propsSmartField} />
                    break;
                case "treatment":
                case "treatment_prescription":
                case "treatment_regular":
                    smartField = <SingleTreatmentSelector {...propsSmartField} />
                    break;
                case "treatment_inpatient":
                    //smartField = <TreatmentInpatient {...propsSmartField} />
                    smartField = <SingleTreatmentSelector {...propsSmartField} />
                    break;
                case "bmi":
                    smartField = <BMIField {...propsSmartField} />
                    break;
                case "medical_history_ai":
                case "medical_history_template":
                case "medical_history_template_fill":
                    smartField = <MedicalHistoryAI {...propsSmartField} 
                                    listElements={listElements} 
                                    uuidInvestigation={props.uuidInvestigation as string} 
                                    formValues={props.formValues} />
                    break;
                case "edd":
                    smartField = <EDDField {...propsSmartField} />
                    break;
                case "template_medical_history":
                    // Get the initial value from existing elements if editing
                    const existingElement = listElements.find((element): element is TemplatedMedicalHistoryType => 
                        'template_medical_history-template_id' in element
                    );
                    const initialValue = existingElement ? existingElement['template_medical_history-notes'] : undefined;
                    
                    // Ensure updateElement is defined for this case
                    if (!propsSmartField.updateElement) {
                        smartField = <div>Missing updateElement function</div>;
                        break;
                    }
                    
                    const templatedProps = {
                        ...propsSmartField,
                        updateElement: propsSmartField.updateElement,
                        uuidInvestigation: props.uuidInvestigation as string,
                        fetchTemplates,
                        fetchTemplate,
                        type: TemplatedMedicalHistoryEditorType.SURGERY_REPORT,
                        initialValue
                    };
                    
                    smartField = <TemplatedMedicalHistoryEditor {...templatedProps} />
                    break;
                case "request_lab":
                case "request_img":
                    if(props.uuidPatient && props.uuidInvestigation && props.uuidSurvey){
                        const serviceType = props.type === "request_lab" ? 0 : 1;
                        smartField = <RequestField cancel={cancel} serviceType={serviceType} uuidInvestigation={props.uuidInvestigation} 
                                        uuidSurvey={props.uuidSurvey}
                                        uuidPatient={props.uuidPatient} {...propsSmartField} />
                    }
                    else{
                        smartField = <div>Missing uuidPatient</div>
                    }
                    break;
                
                default:
                    smartField = "Smarfield not defined"
            }
            return(
                <Grid item xs={12} style={{paddingTop:'1rem'}}>
                    <Typography color="textSecondary" gutterBottom>
                        {
                            props.type !== "template_medical_history" &&
                            <Translate id={`hospital.${props.type}`} />
                        }
                        
                    </Typography>
                    { smartField }
                </Grid>
            ) 
        }
    }

    // useEffect(() => {
    //     console.log("Mounted component");
    // }, []);

    useUpdateEffect(() =>{
        if(props.initialState && props.initialState.listElements.length > 0 ){
            setListElements(props.initialState.listElements);
            setAddingElements(false);
        }
        
    }, [props.initialState]);
    
    useUpdateEffect(() =>{
        if(props.mode === "form"){
            props.elementSelected(listElements);
            if(listElements.length === 0){
                resetState()
            }
        }
        
    }, [listElements]);
    useUpdateEffect(() =>{
        if(!addElement){
            props.elementSelected(false);
        }
    }, [addElement]);



    // Show loader during database operations
    if (loading) {
        return (
            <>
                <Loader />;
            </>
        )
    }
    
    if((!addElement && listElements.length === 0)){
        return (
            <>
                {renderSelect()}
            </>
        );
    }
    else{
        return (
            <>
                {/*Stop modal*/}
                {showStopModal && (
                    <Modal 
                        open={showStopModal}
                        title={<Translate id="hospital.treatment-stop-title" />}
                        closeModal={cancelStopTreatment}
                        confirmAction={stopMotive.trim() ? confirmStopTreatment : undefined}
                        confirmButtonLabel="general.confirm"
                        maxWidth="sm"
                    >
                        <Box sx={{ p: 2, minWidth: 300 }}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label={<Translate id="hospital.treatment-stop-motive" />}
                                value={stopMotive}
                                onChange={(e) => setStopMotive(e.target.value)}
                                variant="outlined"
                                placeholder={props.translate("hospital.treatment-stop-placeholder")}
                                error={showStopModal && !stopMotive.trim()}
                                helperText={showStopModal && !stopMotive.trim() ? props.translate("hospital.treatment-stop-required") : ""}
                                required
                            />
                        </Box>
                    </Modal>
                )}
                {/*Remove modal*/}
                {showRemoveModal && (
                    <Modal 
                        open={showRemoveModal}
                        title={<Translate id="hospital.treatment-remove-title" />}
                        closeModal={cancelRemoveTreatment}
                        confirmAction={removeMotive.trim() ? confirmRemoveTreatment : undefined}
                        confirmButtonLabel="general.confirm"
                        maxWidth="sm"
                    >
                        <Box sx={{ p: 2, minWidth: 300 }}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label={<Translate id="hospital.treatment-remove-motive" />}
                                value={removeMotive}
                                onChange={(e) => setRemoveMotive(e.target.value)}
                                variant="outlined"
                                placeholder={props.translate("hospital.treatment-remove-placeholder")}
                                error={showRemoveModal && !removeMotive.trim()}
                                helperText={showRemoveModal && !removeMotive.trim() ? props.translate("hospital.treatment-remove-required") : ""}
                                required
                            />
                        </Box>
                    </Modal>
                )}

                <Grid item xs={12} style={{paddingTop:'1rem'}}>
                        <Card variant="outlined">
                            <CardContent>
                            {
                                (!addingElements && props.mode === "form" && !SINGLE_SMARTFIELDS.includes(props.type)) &&
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box>
                                            <ButtonPlus onClick={() =>  setAddingElements(true)} />
                                            <Typography variant="body2" component="span">{props.translate(`hospital.add-${props.type}`)}</Typography>
                                        </Box>
                                        {/* Filter checkbox for treatment_inpatient */}
                                        {props.type === "treatment_inpatient" && (
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={showOnlyActiveTreatments}
                                                        onChange={(e) => setShowOnlyActiveTreatments(e.target.checked)}
                                                        size="small"
                                                    />
                                                }
                                                label={<Translate id="hospital.show-only-active-treatments" />}
                                            />
                                        )}
                                    </Box>
                                </Grid>
                            }
                            {
                                renderSelector()
                            }
                            {
                                renderElements()
                            }
                            </CardContent>
                    </Card>
                </Grid>

                {/* Snackbar for feedback */}
                <Snackbar 
                    open={showSnackbar.show} 
                    autoHideDuration={4000} 
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert 
                        onClose={handleCloseSnackbar} 
                        severity={showSnackbar.severity || 'success'} 
                        sx={{ width: '100%' }}
                    >
                        {showSnackbar.message}
                    </Alert>
                </Snackbar>

            </>
        )
    }
    
}
export default withLocalize(SmartField);